/**
 * `/leadership-report` — the HelpDesk manager overview, online (Rusty, 8–9 Oct).
 *
 * For the IT Leadership Team only (members of the Ivanti team
 * `LEADERSHIP_REPORT_TEAM`, the allowlist `LEADERSHIP_REPORT_VIEWERS`, and
 * Rusty; `/auth/me` → `leadership_report`; the server answers 403 to anyone
 * else). The backend rebuilds the overview from its own Ivanti, GET only,
 * three times a day (12 Oct: 07:00 Denmark, 07:00 and 12:00 US Eastern); the
 * line above the report says when the data was pulled and when the next
 * update is (ET and Denmark), or that the last one failed (the last good
 * report stays up). Rusty alone also gets "Update now" (`refresh-line.tsx`).
 * Earlier copies are in the date picker (every pull for 7 days, then one a
 * day for 90 days).
 *
 * The report is rendered in a sandboxed frame (`srcdoc`, `sandbox="allow-scripts"`
 * without `allow-same-origin`): it runs in an opaque origin and cannot reach
 * the app, its cookies or the API. The app's Content-Security-Policy is
 * inherited by a `srcdoc` frame, so inline report scripts are blocked; the
 * overview is static HTML and SVG, which is unaffected.
 *
 * 12 Oct: the main view is now the interactive page (`insights-view.tsx`):
 * every number recomputed for a date range and filters, clickable down to
 * the tickets, with Excel and PDF downloads. The stored HTML overview stays
 * as "Printable version" (the second tab). Range, filters and tab are in the
 * URL, so a view can be shared.
 *
 * 16 Oct: "Ask about this report" (header button, interactive view only) opens
 * a side panel (`ask-report-panel.tsx`) that answers questions from the
 * report's data for the range and filters on screen. "Add a note" (header,
 * and under each answer) replaces "Ask Rusty a question": a note or
 * suggestion for the IT HelpDesk, stored in the Feedback inbox
 * (`note-dialog.tsx`).
 */

import { Download, Icon, NotebookPen, Printer, Sparkles } from "@norican/icons";
import { Alert, Button, Select, Tabs } from "@norican/react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import { logger } from "@/lib/logger";

import { useReportRefreshStatus, useReportSnapshot, useReportSnapshots } from "./api";
import { downloadInsightsExcel, useReportInsights } from "./insights-api";
import { type InsightsState, parseState, type ReportView, stateToSearch } from "./insights-helpers";
import { InsightsView } from "./insights-view";
import { type NoteContext, NoteDialog } from "./note-dialog";
import { RefreshLine, UpdateNowButton } from "./refresh-line";
import {
  etTime,
  HELPDESK_OVERVIEW,
  pickSnapshotId,
  refreshFailed,
  REPORT_FRAME_SANDBOX,
  snapshotOptions,
} from "./report-helpers";

export function LeadershipReportPage() {
  const { t } = useT("common");
  useDocumentTitle(t("leadershipReport.documentTitle"));
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseState(params), [params]);
  const setState = useCallback(
    (next: InsightsState) => setParams(stateToSearch(next), { replace: true }),
    [setParams],
  );
  const live = state.view === "live";
  const insights = useReportInsights(HELPDESK_OVERVIEW, state);
  const snapshots = useReportSnapshots(HELPDESK_OVERVIEW);
  const [chosen, setChosen] = useState<number | null>(null);
  const printableId = pickSnapshotId(snapshots.data?.items ?? [], chosen);
  const snapshotId = live ? (insights.data?.meta.snapshot_id ?? null) : printableId;
  const [noting, setNoting] = useState(false);
  const [noteContext, setNoteContext] = useState<NoteContext | null>(null);
  const [askingReport, setAskingReport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  async function excel() {
    setExporting(true);
    setExportFailed(false);
    try {
      await downloadInsightsExcel(HELPDESK_OVERVIEW, state);
    } catch (error) {
      logger.warn("[leadership-report] Excel download failed", error);
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  }

  const actions = (
    <div className="flex flex-wrap gap-2" data-print-hide>
      <UpdateNowButton />
      {live ? (
        <>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            startIcon={<Icon icon={Download} size="sm" />}
            disabled={!insights.data || exporting}
            onClick={() => void excel()}
          >
            {exporting ? t("leadershipReport.live.exporting") : t("leadershipReport.live.excel")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            startIcon={<Icon icon={Printer} size="sm" />}
            disabled={!insights.data}
            onClick={() => window.print()}
          >
            {t("leadershipReport.live.pdf")}
          </Button>
        </>
      ) : null}
      {live ? (
        <Button
          size="sm"
          variant="outline"
          startIcon={<Icon icon={Sparkles} size="sm" />}
          disabled={!insights.data}
          onClick={() => setAskingReport(true)}
        >
          {t("leadershipReport.askReport.button")}
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        tone="neutral"
        startIcon={<Icon icon={NotebookPen} size="sm" />}
        onClick={() => {
          setNoteContext(null);
          setNoting(true);
        }}
      >
        {t("leadershipReport.note.button")}
      </Button>
    </div>
  );

  return (
    <PageContainer
      title={t("leadershipReport.title")}
      subtitle={t("leadershipReport.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("leadershipReport.title") }]} />}
      actions={actions}
      width="wide"
    >
      {exportFailed ? (
        <Alert tone="danger" title={t("leadershipReport.live.exportFailed")} />
      ) : null}
      <Tabs.Root
        value={state.view}
        onValueChange={(view) => setState({ ...state, view: view as ReportView })}
      >
        <Tabs.List aria-label={t("leadershipReport.live.tabs")} data-print-hide>
          <Tabs.Tab value="live">{t("leadershipReport.live.tabLive")}</Tabs.Tab>
          <Tabs.Tab value="printable">{t("leadershipReport.live.tabPrintable")}</Tabs.Tab>
          <Tabs.Indicator />
        </Tabs.List>
        <Tabs.Panel value="live" className="pt-4">
          {live ? (
            <InsightsView
              state={state}
              onChange={setState}
              askOpen={askingReport}
              onAskOpenChange={setAskingReport}
              onAddNote={(context) => {
                setNoteContext(context);
                setNoting(true);
              }}
            />
          ) : null}
        </Tabs.Panel>
        <Tabs.Panel value="printable" className="pt-4">
          {!live ? <PrintableView selectedId={printableId} onChoose={setChosen} /> : null}
        </Tabs.Panel>
      </Tabs.Root>
      <NoteDialog
        open={noting}
        onOpenChange={setNoting}
        state={state}
        snapshotId={snapshotId}
        context={noteContext}
      />
    </PageContainer>
  );
}

/** The stored HTML overview, as before 12 Oct: a date picker and the sandboxed frame. */
function PrintableView({
  selectedId,
  onChoose,
}: {
  selectedId: number | null;
  onChoose: (id: number) => void;
}) {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const snapshots = useReportSnapshots(HELPDESK_OVERVIEW);
  const status = useReportRefreshStatus(HELPDESK_OVERVIEW);
  const items = useMemo(() => snapshots.data?.items ?? [], [snapshots.data]);
  const snapshot = useReportSnapshot(HELPDESK_OVERVIEW, selectedId);

  const options = useMemo(
    () =>
      snapshotOptions(
        items,
        (iso) => etTime(iso, locale) ?? iso,
        (label) => t("leadershipReport.snapshot.latest", { date: label }),
      ),
    [items, locale, t],
  );

  const failedAt = status.data && refreshFailed(status.data) ? status.data.last_failure_at : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {options.length > 0 ? (
          <div className="w-64">
            <Select.Root
              value={selectedId === null ? null : String(selectedId)}
              items={options}
              onValueChange={(value) => onChoose(Number(value))}
            >
              <Select.Trigger
                placeholder={t("leadershipReport.snapshot.label")}
                aria-label={t("leadershipReport.snapshot.label")}
              />
              <Select.Portal>
                <Select.Positioner sideOffset={4}>
                  <Select.Popup width="anchor">
                    <Select.List>
                      {options.map((option) => (
                        <Select.Item key={option.value} value={option.value}>
                          {option.label}
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </div>
        ) : null}
        <RefreshLine pulledAt={snapshot.data?.generated_at} />
      </div>
      {failedAt ? (
        <Alert
          tone="warning"
          title={t("leadershipReport.status.failed", { time: etTime(failedAt, locale) ?? "" })}
        />
      ) : null}

      {snapshots.isLoading || (selectedId !== null && snapshot.isLoading) ? (
        <LoadingRows rows={8} />
      ) : snapshots.error != null ? (
        <ErrorState error={snapshots.error} onRetry={() => void snapshots.refetch()} />
      ) : snapshot.error != null ? (
        <ErrorState error={snapshot.error} onRetry={() => void snapshot.refetch()} />
      ) : snapshot.data ? (
        <iframe
          title={t("leadershipReport.frameTitle")}
          srcDoc={snapshot.data.html}
          sandbox={REPORT_FRAME_SANDBOX}
          referrerPolicy="no-referrer"
          className="border-border bg-bg h-[80vh] w-full rounded-md border"
        />
      ) : (
        <EmptyStateBlock
          title={t("leadershipReport.empty.title")}
          description={t("leadershipReport.empty.hint")}
        />
      )}
    </div>
  );
}
